import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from "react";
import { SHIPPING, FREE_SHIPPING_THRESHOLD, PROMO } from "../data/products";

const CartContext = createContext(null);

const STORAGE_KEY = "balance_cart";

function loadCart() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(loadCart);
  const [open, setOpen] = useState(false);
  const [promo, setPromo] = useState("");

  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("promo");
    if (p) setPromo(p);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      console.warn("Cart persistence failed (quota/private mode):", e);
    }
  }, [items]);

  const addItem = useCallback((product) => {
    setItems((prev) => {
      const key = product.id + (product.variant || "");
      const found = prev.find((i) => i.key === key);
      if (found) {
        return prev.map((i) => (i.key === key ? { ...i, qty: i.qty + 1 } : i));
      }
      return [
        ...prev,
        {
          key,
          id: product.id,
          name: product.name,
          variant: product.variant || null,
          price: product.price,
          qty: 1,
          img: product.img,
          freeShip: product.freeShip || false,
          ship_dose: product.ship_dose || null,
        },
      ];
    });
    setOpen(true);
  }, []);

  const removeItem = useCallback((key) => {
    setItems((prev) => prev.filter((i) => i.key !== key));
  }, []);

  const setQty = useCallback((key, qty) => {
    setItems((prev) =>
      prev
        .map((i) => (i.key === key ? { ...i, qty: Math.max(1, qty) } : i))
        .filter((i) => i.qty > 0)
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const subtotal = useMemo(
    () => items.reduce((s, i) => s + i.price * i.qty, 0),
    [items]
  );
  const count = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);
  const shipping = items.length > 0 && subtotal < FREE_SHIPPING_THRESHOLD && !items.some((i) => i.freeShip) ? SHIPPING : 0;
  const promoValid = promo.trim().toUpperCase() === PROMO.code && items.length > 0;
  const discount = promoValid ? Math.min(PROMO.amount, subtotal) : 0;
  const total = subtotal + shipping - discount;

  const value = {
    items,
    open,
    setOpen,
    addItem,
    removeItem,
    setQty,
    clear,
    subtotal,
    shipping,
    discount,
    promo,
    setPromo,
    promoValid,
    total,
    count,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
